import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Drawer, Typography, Tooltip } from '@arco-design/web-react';
import { BsQuestionCircle, BsBraces, BsEmojiSmile } from 'react-icons/bs';
import { useForm, useFormState } from 'react-final-form';
import { BayEmojiPicker } from '@bay-ui';
import BayButton from '@bay-ui/BayAddButton';
import { TiptapInput, TiptapInputRef } from './TiptapInput';
import { t, getMergeTagLabel } from '@bay-email/core';
import { MergeTags } from '@extensions/AttributePanel/components/attributes/MergeTags';
import styles from './index.module.scss';

interface SubjectPreviewDrawerProps {
	visible: boolean;
	onClose: () => void;
	onSave?: (subject: string, previewText: string) => void;
	openFromModal?: boolean;
}

export function SubjectPreviewDrawer({
	visible,
	onClose,
	onSave,
	openFromModal = false,
}: SubjectPreviewDrawerProps) {
	const form = useForm();
	const formState = useFormState();

	const [subjectValue, setSubjectValue] = useState('');
	const [previewValue, setPreviewValue] = useState('');
	const [subjectCharCount, setSubjectCharCount] = useState(0);
	const [previewCharCount, setPreviewCharCount] = useState(0);

	const subjectInputRef = useRef<TiptapInputRef>(null);
	const previewInputRef = useRef<TiptapInputRef>(null);

	// Load current values when drawer opens
	useEffect(() => {
		if (visible) {
			const currentSubject = formState.values?.subject || '';
			const currentPreview = formState.values?.subTitle || '';

			setSubjectValue(currentSubject);
			setPreviewValue(currentPreview);
		}
	}, [visible, formState.values?.subject, formState.values?.subTitle]);

	const handleMergeTagSelect = (field: 'subject' | 'preview') => (tagString: string) => {
		const key = tagString.replace(/^\{\{|\}\}$/g, '');
		const label = getMergeTagLabel(key) || key.split('.').pop() || key;
		if (field === 'subject') {
			subjectInputRef.current?.insertMention({ value: key, label });
		} else {
			previewInputRef.current?.insertMention({ value: key, label });
		}
	};

	const handleSave = () => {
		form.change('subject', subjectValue);
		form.change('subTitle', previewValue);

		// Call the onSave callback if provided to update Redux store
		if (onSave) {
			onSave(subjectValue, previewValue);
		}

		onClose();
	};

	const handleCancel = () => {
		onClose();
	};

	const handleEmojiSelect = useCallback((field: 'subject' | 'preview', emoji: string) => {
		if (field === 'subject') {
			subjectInputRef.current?.insertEmoji(emoji);
		} else {
			previewInputRef.current?.insertEmoji(emoji);
		}
	}, []);

	return (
		<Drawer
			width={570}
			{...(openFromModal && { zIndex: 1055 })}
			className={styles.subjectPreviewDrawer}
			title={<h1 className={styles.drawerTitle}>Email Subject & Preview Text</h1>}
			visible={visible}
			onCancel={handleCancel}
			footer={
				<div className='d-flex justify-content-between gap-3'>
					<BayButton
						showIcon={false}
						text='Cancel'
						onClick={handleCancel}
						variant='outline-secondary'
					/>
					<BayButton
						disabled={!subjectValue?.trim()}
						showIcon={false}
						text='Apply'
						onClick={handleSave}
					/>
				</div>
			}
			wrapClassName={styles.subjectPreviewDrawerWrapper}
		>
			<div>
				{/* Subject Field */}
				<div className='mb-3'>
					<div className='d-flex align-items-center justify-content-between mb-2'>
						<div className='d-flex align-items-center'>
							<Typography.Text className='fw-bold'>Subject</Typography.Text>
							<span className='text-danger'>*</span>
						</div>
						<div className='d-flex align-items-center gap-2'>
							<MergeTags
								value=''
								showSearch
								context='subject-preview'
								onChange={handleMergeTagSelect('subject')}
							>
								<span className='dynamicFieldDropdown d-flex align-items-center gap-1 fs-7 text-secondary cursor-pointer'>
									<span className='text-secondary cursor-pointer d-flex gap-2 align-items-center fs-6'>
										<BsBraces size={16} className='text-700' />
										<span className='dynamic-field'>{t('Dynamic Fields')}</span>
									</span>
								</span>
							</MergeTags>
							<Tooltip
								className={styles.drawerTooltip}
								content={t('Personalized emails get higher open rates. Insert dynamic fields to automatically pull in unique recipient details in your subject and preview text fields.')}
								getPopupContainer={() => document.body}
								position='tr'
							>
								<BsQuestionCircle size={16} className='text-700 cursor-pointer' />
							</Tooltip>
						</div>
					</div>
					<div className='position-relative'>
						<TiptapInput
							ref={subjectInputRef}
							value={subjectValue}
							onChange={setSubjectValue}
							placeholder='Enter subject'
							characterLimit={50}
							onCharacterCount={setSubjectCharCount}
						/>
						<div
							className='position-absolute top-50 end-0 translate-middle-y d-flex align-items-center gap-2 pe-3'
							style={{ pointerEvents: 'none' }}
						>
							<div style={{ pointerEvents: 'auto' }}>
								<BayEmojiPicker
									className='d-flex'
									onEmojiSelect={(emoji: string) => handleEmojiSelect('subject', emoji)}
								>
									<BsEmojiSmile size={16} className='cursor-pointer text-700'/>
								</BayEmojiPicker>
							</div>
							<span className='text-500'>{subjectCharCount}/50</span>
						</div>
					</div>
					{!subjectValue?.trim() && <div className='d-flex invalid-feedback'>Email Subject cannot be empty</div>}
					<div className='mt-2 px-3 py-2 bg-50 rounded text-center lh-1'>
						<span className={`text-500 ${styles.drawerNote}`}>
							💡 Aim for 30-50 characters. This helps prevent your subject
							<br />
							from being cut off, especially on mobile!
						</span>
					</div>
				</div>

				{/* Preview Text Field */}
				<div className='mb-3'>
					<div className='d-flex align-items-center justify-content-between mb-2'>
						<div className='d-flex gap-2 align-items-center'>
							<Typography.Text className='fw-bold'>Preview Text</Typography.Text>
							<Tooltip
								className={styles.drawerTooltip}
								content={
									<>
										<div className='mb-4'>{t('This text appears alongside your subject line in most inboxes. Use it to encourage opens!')}</div>
										<div>{t('If left blank, email clients will automatically pull in the first few lines of text from your email body.')}</div>
									</>
								}
								getPopupContainer={() => document.body}
							>
								<BsQuestionCircle size={16} className='text-700 cursor-pointer'/>
							</Tooltip>
						</div>
						<div className='d-flex align-items-center gap-2'>
							<MergeTags
								value=''
								showSearch
								context='subject-preview'
								onChange={handleMergeTagSelect('preview')}
							>
								<span className='dynamicFieldDropdown d-flex align-items-center gap-1 fs-7 text-secondary cursor-pointer'>
									<span className='text-secondary cursor-pointer d-flex gap-2 align-items-center fs-6'>
										<BsBraces size={16} className='text-700' />
										<span className='dynamic-field'>{t('Dynamic Fields')}</span>
									</span>
								</span>
							</MergeTags>
							<Tooltip
								className={styles.drawerTooltip}
								content={t('Personalized emails get higher open rates. Insert dynamic fields to automatically pull in unique recipient details in your subject and preview text fields.')}
								getPopupContainer={() => document.body}
								position='tr'
							>
								<BsQuestionCircle size={16} className='text-700 cursor-pointer'/>
							</Tooltip>
						</div>
					</div>
					<div className='position-relative'>
						<TiptapInput
							ref={previewInputRef}
							value={previewValue}
							onChange={setPreviewValue}
							placeholder='Enter preview text'
							characterLimit={100}
							onCharacterCount={setPreviewCharCount}
						/>
						<div
							className='position-absolute top-50 end-0 translate-middle-y d-flex align-items-center gap-2 pe-3'
							style={{ pointerEvents: 'none' }}
						>
							<div style={{ pointerEvents: 'auto' }}>
								<BayEmojiPicker
									className='d-flex'
									onEmojiSelect={(emoji: string) => handleEmojiSelect('preview', emoji)}
								>
									<BsEmojiSmile size={16} className='cursor-pointer text-700'/>
								</BayEmojiPicker>
							</div>
							<span className='text-500'>{previewCharCount}/100</span>
						</div>
					</div>
					<div className='mt-2 px-3 py-2 bg-50 rounded text-center lh-1'>
						<span className={`text-500 ${styles.drawerNote}`}>
							💡 85-100 characters is the sweet spot! It's the perfect length to
							<br />
							give a great preview without being truncated.
						</span>
					</div>
				</div>
			</div>
		</Drawer>
	);
}
